import React, { ReactNode, useRef, useState } from 'react';

import { NavLink } from '~/lib/react-router-dom';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  Box,
  Button,
  ClickAwayListener,
  Divider,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  useMediaQuery,
} from '@mui/material';

import useHoverMenu from '~/hooks/useHoverMenu';

interface NavDropdownItem {
  href: string;
  label: string;
  icon?: ReactNode;
  section?: string;
}

interface NavDropdownProps {
  buttonLabel: string;
  items: Array<NavDropdownItem>;
}

export default function NavDropdown({ buttonLabel, items }: NavDropdownProps) {
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
  const [wasOpenedWithKeyboard, setWasOpenedWithKeyboard] = useState(false);
  const menuTriggerRef = useRef<HTMLButtonElement>(null);
  const isHoverCapable = useMediaQuery('(hover: hover)');
  const isOpen = Boolean(anchorElement);

  const openMenu = (openedWithKeyboard = false) => {
    setWasOpenedWithKeyboard(openedWithKeyboard);
    setAnchorElement(menuTriggerRef.current);
  };

  const closeMenu = () => {
    setAnchorElement(null);
  };

  const hoverMenuHandlers = useHoverMenu(() => openMenu(), closeMenu);

  const openMenuWithKeyboard = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (!['Enter', ' ', 'ArrowDown'].includes(event.key)) {
      return;
    }

    event.preventDefault();
    openMenu(true);
  };

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
      <Button
        ref={menuTriggerRef}
        color="inherit"
        endIcon={<KeyboardArrowDownIcon />}
        onClick={() => openMenu()}
        onKeyDown={openMenuWithKeyboard}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        sx={{ textTransform: 'none' }}
        {...hoverMenuHandlers}
      >
        {buttonLabel}
      </Button>

      <ClickAwayListener onClickAway={closeMenu}>
        <Menu
          open={isOpen}
          anchorEl={anchorElement}
          autoFocus={wasOpenedWithKeyboard}
          onClose={closeMenu}
          MenuListProps={{ ...hoverMenuHandlers, autoFocusItem: wasOpenedWithKeyboard }}
          sx={
            isHoverCapable
              ? {
                  pointerEvents: 'none',
                  '& .MuiMenu-paper': { pointerEvents: 'auto' },
                }
              : undefined
          }
        >
          {items.flatMap((item, index) => {
            const isNewSection = item.section && (index === 0 || items[index - 1].section !== item.section);

            return [
              ...(isNewSection && index > 0 ? [<Divider key={`divider-${item.section}`} />] : []),
              ...(isNewSection ? [<ListSubheader key={`section-${item.section}`}>{item.section}</ListSubheader>] : []),
              <MenuItem key={`${item.href}-${item.label}`} onClick={closeMenu} to={item.href} component={NavLink}>
                {item.icon && <ListItemIcon>{item.icon}</ListItemIcon>}
                <ListItemText>{item.label}</ListItemText>
              </MenuItem>,
            ];
          })}
        </Menu>
      </ClickAwayListener>
    </Box>
  );
}
